import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu as MantineMenu } from '@mantine/core';

import { Button } from '../buttons/Button';
import { SVGIcon } from '../icons/SVGIcon';
import { Menu } from '../menu/Menu';
import {
  UserSettingsModal,
  type UserSettingsTenantDefaults,
} from '../user-settings-modal/UserSettingsModal';

import styles from './userMenu.module.scss';

export type UserMenuLinkItem = {
  label: string;
  link: string;
};

export type UserMenuProps = {
  tenantDefaults?: UserSettingsTenantDefaults;
  onLogout?: () => void;
  onChangePassword?: () => void;
  onLanguageSaved?: (language: string) => void;
  userCanSwitchTheme?: boolean;
  userEmail?: string;
  portalTarget?: string | HTMLElement;
  menuItems?: UserMenuLinkItem[];
};

export function UserMenu({
  tenantDefaults,
  onLogout,
  onChangePassword,
  onLanguageSaved,
  userCanSwitchTheme = false,
  userEmail,
  menuItems: linkMenuItems = [],
}: UserMenuProps) {
  const { t } = useTranslation();
  const [settingsOpened, setSettingsOpened] = useState(false);

  const logoutItem = t('app.logout');
  const settingsItem = t('app.settings');

  const actionMenuLabels: string[] = [
    settingsItem,
    onLogout ? logoutItem : undefined,
  ].filter((item) => item != null);

  const onSelect = async (selectedItem: string) => {
    if (selectedItem === logoutItem) {
      await onLogout?.();
    } else if (selectedItem === settingsItem) {
      setSettingsOpened(true);
    }
  };

  return (
    <>
      <Menu
        menuTarget={
          <Button icon={<SVGIcon name="user" />} variant="secondary" />
        }
        menuDropdown={
          <>
            {linkMenuItems.map((item) => (
              <MantineMenu.Item
                className={styles['menu-item']}
                component="a"
                href={item.link}
                key={`${item.label}-${item.link}`}
              >
                {item.label}
              </MantineMenu.Item>
            ))}
            {actionMenuLabels.map((item) => (
              <MantineMenu.Item
                className={styles['menu-item']}
                key={item}
                onClick={() => onSelect(item)}
              >
                {item}
              </MantineMenu.Item>
            ))}
          </>
        }
        width={238}
      />
      <UserSettingsModal
        tenantDefaults={tenantDefaults}
        onChangePassword={onChangePassword}
        onClose={() => setSettingsOpened(false)}
        onLanguageSaved={onLanguageSaved}
        opened={settingsOpened}
        userCanSwitchTheme={userCanSwitchTheme}
        userEmail={userEmail}
      />
    </>
  );
}
